import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { faSortUp, faSortDown, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Loader, Spinner } from '../Loader/Loader';
import useCommonResponseHandler from '../CommenResponseAndErrorHandlers/CommenResponseHandler';
import Search from '../Components/Search';
import Pagination, { usePagination } from '../Components/Pagination';
import { decryptData } from '../../utils/storageCrypto';

const VehicleScheduleList = () => {
  const fetchUrl = process.env.REACT_APP_FORE_BRICKBUCKET;
  const [vehicleSchedule, setVehicleSchedule] = useState([]);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [searchChips, setSearchChips] = useState(() => {
    const saved = sessionStorage.getItem('vsSearchChips');
    return saved ? JSON.parse(saved) : [];
  });
  const { 
    showPopup, popupMessage, popupType, retryFunction, handleGetResponse, handleSaveResponse, setShowPopup,
    isSaving, setRetryFunction, handleDeleteResponse, setIsSaving, setPopupMessage,
    setPopupType 
  } = useCommonResponseHandler();

  const RETRY_LIMIT = 3;
  const [retryCount, setRetryCount] = useState(0);
  const [filteredVS, setFilteredVS] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const searchTypes = ['Schedule Number', 'Vehicle Number', 'Driver Name', 'Driver Number', 'Status'];

  const hasActiveSearch = searchChips.length > 0 || searchQuery.trim() !== '';
  const schedulesToDisplay = hasActiveSearch ? filteredVS : vehicleSchedule;

  const {
    currentItems: currentSchedules,
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    handlePageChange,
    handlePreviousPage,
    handleNextPage,
    resetToFirstPage
  } = usePagination(schedulesToDisplay, 10);

  const sortArray = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });

    const sortedArray = [...schedulesToDisplay].sort((a, b) => {
      const aValue = key.split('.').reduce((obj, k) => obj && obj[k], a) || '';
      const bValue = key.split('.').reduce((obj, k) => obj && obj[k], b) || '';
      
      const aString = aValue.toString().toLowerCase();
      const bString = bValue.toString().toLowerCase();

      if (aString < bString) return direction === 'ascending' ? -1 : 1;
      if (aString > bString) return direction === 'ascending' ? 1 : -1;
      return 0;
    });

    if (hasActiveSearch) {
      setFilteredVS(sortedArray);
    } else {
      setVehicleSchedule(sortedArray);
    }

    resetToFirstPage();
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'ascending' ? faSortUp : faSortDown;
    }
    return null;
  };

  const handleCreate = () => {
    navigate("/bb/app/sales/newvehicleschedule");
  };

  const handleSearch = (query, type) => {
    if (!type && query === '') {
      setFilteredVS(vehicleSchedule);
       resetToFirstPage(); 
      return;
    }

    const filtered = vehicleSchedule.filter((schedule) => {
      const lowercaseQuery = (query || '').toLowerCase();
      switch (type) {
        case 'Schedule Number':
          return schedule?.schedule_number?.toLowerCase().includes(lowercaseQuery);
        case 'Vehicle Number':
          return schedule?.vehicle_number?.toLowerCase().includes(lowercaseQuery);
        case 'Driver Name':
          return schedule?.driver_name?.toLowerCase().includes(lowercaseQuery);
        case 'Driver Number':
          return schedule?.driver_number?.toLowerCase().includes(lowercaseQuery);
        case 'Status':
          return schedule?.status?.toLowerCase().includes(lowercaseQuery);
        default:
          return false;
      }
    });

    setFilteredVS(filtered);
    resetToFirstPage(); 
  };

  const handleRemoveSearchChip = (chipToRemove) => {
    const updated = searchChips.filter(
      (chip) => !(chip.query === chipToRemove.query && chip.type === chipToRemove.type)
    );
    setSearchChips(updated);
    sessionStorage.setItem('vsSearchChips', JSON.stringify(updated));

    if (updated.length === 0) {
      setFilteredVS(vehicleSchedule);
      setSearchQuery('');
      setSearchType(''); 
    } else {
      const last = updated[updated.length - 1];
      handleSearch(last.query, last.type);
    }
  };

  const handleAddSearchChip = (query, type) => {
    if (!type) return;

    const actualQuery = query.trim(); 

    setSearchChips((prev) => {
      const exists = prev.some((chip) => chip.query === actualQuery && chip.type === type);
      if (exists) return prev;

      const updated = [...prev, { query: actualQuery, type }]; 
      sessionStorage.setItem('vsSearchChips', JSON.stringify(updated)); 
      return updated;
    });

    setSearchQuery(actualQuery); 
    setSearchType(type);        

    handleSearch(actualQuery, type);
  };

  const handleChipClick = (chip) => {
    setSearchType(chip.type);
    setSearchQuery(chip.query);
    handleSearch(chip.query, chip.type);
  };

  const formatScheduleDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const handleNavigateDetails = (schedule) => {
    navigate("/bb/app/sales/vehicleschedulingdetails", { state: { vehicalScheduleData: schedule } });
  };

  useEffect(() => {
    getVehicleschedule();
  }, []);

  useEffect(() => {
    if (vehicleSchedule.length > 0) {
      setFilteredVS(vehicleSchedule);
    }
  }, [vehicleSchedule]);
  
  const getVehicleschedule = async () => {
    try {
     const encryptedToken = sessionStorage.getItem('token');
                const token = decryptData(encryptedToken);
                const encryptedOrganizationId = sessionStorage.getItem('organizationId');
                const organizationId = decryptData(encryptedOrganizationId);
      const response = await fetch(fetchUrl + `customer/vehicle-schedule/get-all-vehicle-schedules?organizationId=${organizationId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token
        }
      });
  
      const data = await response.json();
      const output = await handleGetResponse(response, data);
      if (output) {
        setVehicleSchedule(output);
        console.log(output);
        setIsLoading(false);
      } else {
        console.error(data.message);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch vehicle schedules:', error);
      setPopupMessage('Network error. Please check your connection and try again.');
      setPopupType('error');
      setShowPopup(true);
      setIsLoading(false);
    }
  };
  
  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="bg-white flex flex-row w-full justify-left h-full overflow-y-hidden">
      <div className="flex flex-row w-full">
        <div className="w-full flex-1">
          <div className="flex flex-col w-full justify-center items-center">
            <div className="w-full h-full flex flex-col justify-between">
              <div className="w-full h-16 border-b flex items-center">
                <div className="flex w-full justify-between px-8 items-center">
                  <div className='space-x-10 flex flex-row'>
                    <h1 className="text-lg font-bold whitespace-nowrap">Schedule List</h1>
                    <Search
                      searchQuery={searchQuery}
                      setSearchQuery={setSearchQuery}
                      searchType={searchType}
                      setSearchType={setSearchType}
                      searchTypes={searchTypes}
                      onSearch={handleSearch}
                      onSearchSubmit={handleAddSearchChip}
                      placeholder="Search..."
                    />
                    {searchChips.length > 0 && (
                      <div className="w-full flex flex-cols gap-3 items-center mt-2 px-4">
                        {searchChips.map((chip, index) => {
                          const isActive = chip.type === searchType;

                          const chipClasses = `flex items-center max-w-xs text-sm px-3 py-1 rounded-full shadow-sm transition-all duration-150 ${
                            isActive
                              ? 'bg-red-500 text-white'
                              : 'bg-gray-100 text-gray-800'
                          }`;

                          return (
                            <div
                              key={`${chip.type}-${index}`}
                              className={chipClasses}
                              style={{ whiteSpace: 'nowrap' }}
                              onClick={() => handleChipClick(chip)}
                            >
                              <span className="whitespace-nowrap max-w-[130px]">
                                {chip.query ? chip.query : chip.type}
                              </span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveSearchChip(chip);
                                }}
                                className={`ml-2 text-base font-bold ${
                                  isActive ? 'text-white hover:text-gray-200' : 'text-gray-500 hover:text-red-500'
                                }`}
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleCreate}
                    className="bg-[#F7525A] text-white z-20 px-2 py-1 rounded"
                    title="" data-tooltip="Create Vehicle Shedule"  style={{ '--tooltip-bg': 'black' }}
                  >
                    + New
                  </button>    
                </div>
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={10}
                showInfo={true}
                hideControls={true}
                itemName="vehicle schedules"
              />

              <div className="w-full h-full bg-white rounded-xs overflow-hidden">
                <div className="overflow-y-auto h-[80vh]">
                  {vehicleSchedule.length > 0 ? (
                    <table className="w-full mb-5">
                      <thead className="sticky top-0 bg-[#fafafc] text-[#6c7184] border-b">
                        <tr className='border-b'>
                          <th className="text-xs py-3 text-center cursor-pointer" onClick={() => sortArray('schedule_number')}>
                            Schedule Number {getSortIcon('schedule_number') && <FontAwesomeIcon icon={getSortIcon('schedule_number')} />}
                          </th>
                          <th className="text-xs py-3 text-center cursor-pointer" onClick={() => sortArray('schedule_date')}>
                            Schedule Date {getSortIcon('schedule_date') && <FontAwesomeIcon icon={getSortIcon('schedule_date')} />}
                          </th>
                          <th className="text-xs py-3 text-center cursor-pointer" onClick={() => sortArray('vehicle_number')}>
                            Vehicle Number {getSortIcon('vehicle_number') && <FontAwesomeIcon icon={getSortIcon('vehicle_number')} />}
                          </th>
                          <th className="text-xs py-3 text-center cursor-pointer" onClick={() => sortArray('dc_status')}>
                            DC Status {getSortIcon('dc_status') && <FontAwesomeIcon icon={getSortIcon('dc_status')} />}
                          </th>
                          <th className="text-xs py-3 text-center cursor-pointer" onClick={() => sortArray('driver_name')}>
                            Driver Name {getSortIcon('driver_name') && <FontAwesomeIcon icon={getSortIcon('driver_name')} />}
                          </th>
                          <th className="text-xs py-3 text-center cursor-pointer" onClick={() => sortArray('driver_number')}>
                            Driver Number {getSortIcon('driver_number') && <FontAwesomeIcon icon={getSortIcon('driver_number')} />}
                          </th>
                          <th className="text-xs py-3 text-center cursor-pointer" onClick={() => sortArray('status')}>
                            Status {getSortIcon('status') && <FontAwesomeIcon icon={getSortIcon('status')} />}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="border-b border-t">
                        {currentSchedules.length > 0 ? (
                          currentSchedules.map((schedule) => (
                            <tr key={schedule._id} className="text-sm border-b">
                              <td className="py-2 text-center text-[#408dfb] font-500 cursor-pointer" onClick={() => handleNavigateDetails(schedule)}>
                                {schedule.schedule_number}
                              </td>
                              <td className="py-2 text-center">{formatScheduleDate(schedule.schedule_date)}</td>
                              <td className="py-2 text-center">{schedule.vehicle_number}</td>
                              <td className="py-2 text-center">{schedule.DC_status}</td>
                              <td className="py-2 text-center">{schedule.driver_name}</td>
                              <td className="py-2 text-center">{schedule.driver_number}</td>
                              <td className="py-2 text-center">{schedule.status}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center text-[#6c7184] py-4">
                              <div className="flex flex-col items-center">
                                <FontAwesomeIcon icon={faSearch} className="text-4xl text-gray-300 mb-4" />
                                <p className="text-lg font-medium mb-2">No results found.</p>
                                <p className="text-sm">Try adjusting your search criteria.</p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  ) : (
                    <>
                      <div className="m-4 p-4 text-center border border-grey-500 text-gray-500">
                        No Scheduled Vehicles Found.
                      </div>
                      <div className="flex justify-center items-center mb-4">
                        <h1 className="text-xl font-bold">Create Vehicle Schedule</h1>
                      </div>
                      <div className="flex justify-center items-center">
                        <button className="bg-[#F7525A] text-white px-2 py-1 rounded-md" onClick={handleCreate}>Create</button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="sticky bottom-0 bg-white shadow-md z-10 px-4 py-2">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={10}
                  onPageChange={handlePageChange}
                  onPreviousPage={handlePreviousPage}
                  onNextPage={handleNextPage}
                  itemName="vehicle schedules"
                  showInfo={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleScheduleList;
